import { action, observable, runInAction, computed } from 'mobx';
import { find, findIndex, sortBy, omit, NumericDictionary } from 'lodash';
import API from '../core/api';
import Organization from '../models/organization';

export default class OrganizationStore {
  @observable organizations: Organization[] = [];
  @observable selectedOrganizationId: number;
  @observable loadingOrganizations = false;
  @observable divisionToOrganization: NumericDictionary<number> = {};

  findOrganization(id: number) {
    return find(this.organizations, (h) => h.id === id);
  }

  @computed
  get organizationsSortedByName() {
    return sortBy(this.organizations, 'name');
  }

  @computed
  get selectedOrganization() {
    return this.findOrganization(this.selectedOrganizationId);
  }

  @action
  clearOrganizations() {
    this.organizations = [];
  }

  @action
  async loadOrganizations(all?: boolean) {
    this.loadingOrganizations = true;
    const result = await API.queryList(all ? 'allOrganizations' : 'organizations', Organization);
    return await runInAction(() => {
      this.loadingOrganizations = false;
      return (this.organizations = result.data);
    });
  }

  @action
  async loadOrganization(organizationId) {
    const result = await API.query('showOrganization', Organization, {
      organizationId,
    });
    return await runInAction(() => this.updateOrganization(result.data));
  }

  @action
  addOrganization(organization: Organization) {
    return this.updateOrganization(organization);
  }

  @action
  updateOrganization(organization: Organization) {
    const existingIndex = findIndex(this.organizations, (o) => o.id === organization.id);
    if (existingIndex === -1) {
      this.organizations.push(organization);
    } else {
      this.organizations[existingIndex] = organization;
    }
    return organization;
  }

  @action.bound
  clearSelectedOrganization() {
    this.selectedOrganizationId = null;
  }

  @action.bound
  selectOrganization(organization: Organization) {
    this.selectedOrganizationId = organization && organization.id;

    return organization;
  }

  @action.bound
  async selectOrganizationById(orgId: number) {
    let org = this.findOrganization(orgId);

    if (!org) {
      org = this.addOrganization(await this.loadOrganization(orgId));
    }

    return this.selectOrganization(org);
  }

  @action
  async selectOrganizationByDivisionId(tsDivisionId: number) {
    const orgId = this.divisionToOrganization[tsDivisionId];

    if (orgId) {
      this.selectedOrganizationId = orgId;
      return orgId;
    }

    const result = await API.query<Organization>('getByTsDivisionId', ['id'], {
      tsDivisionId,
    });
    return runInAction(() => {
      this.selectedOrganizationId = result.data.id;
      return (this.divisionToOrganization[tsDivisionId] = result.data.id);
    });
  }
}
